import type { ReactNode } from 'react';

import { ApiError } from '@shared/api/client';
import type { ErrorCopy } from '@shared/errors/catalog';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatNumber } from '@shared/format';
import { cn } from '@shared/ui/cn';
import { ErrorBanner } from '@shared/ui/ErrorBanner';
import { StateBadge } from '@shared/ui/StateBadge';

import { roleLabel, sharedCopy } from './copy';

/**
 * The fragments both account surfaces render the same way.
 *
 * `SurfaceError` and `TruncationLine` are the **second** occurrence of a shape
 * the subscription area already carries. The rule is extraction on the third,
 * not the second, so they stay local here and the promotion to `@shared/ui/` is
 * due when a third area needs them — which the moderation and people surfaces
 * will make true.
 */

/**
 * The neutral face.
 *
 * A gradient rather than an initial: an Arabic letter changes shape with
 * joining, so a one-letter monogram reads as a different letter than the name
 * it stands for. Built from the identity tokens through `color-mix` rather than
 * from a raw hex, because a literal colour in component code forks the source
 * and silently loses the second theme.
 *
 * `size-6` and `size-9` are the spacing scale's `2rem` and `4rem` — this repo's
 * Tailwind scale maps onto the design system's steps, so the numbers are token
 * indices rather than a `rem` sweep.
 */
export function Avatar({ large = false }: { large?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        'shrink-0 rounded-full border border-border',
        '[background:linear-gradient(135deg,color-mix(in_srgb,var(--brand-noir)_78%,var(--brand-bone)_22%),color-mix(in_srgb,var(--brand-noir)_92%,var(--brand-bone)_8%))]',
        large ? 'size-9' : 'size-6',
      )}
    />
  );
}

/**
 * The account's role.
 *
 * **One badge, because there is one role.** This drew a second one beside it for
 * the editorial-reviewer grant, read off `auth__users.admin_roles` — a registry
 * `backend-auth` keeps as a **UI hint** its owning service is required to
 * re-check, and one whose `content` key now always agrees with the account role
 * anyway. Rendering a role from the hint is how a roster comes to show a grant
 * the owning service does not hold.
 *
 * `neutral`. A granted role is not a state and takes no colour of its own —
 * `success` would say that holding a role is an achievement the platform is
 * congratulating.
 */
export function RoleBadges({ role }: { role: string }) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <StateBadge kind="neutral">{roleLabel(role)}</StateBadge>
    </span>
  );
}

/**
 * The account's standing.
 *
 * `success` on a working account and `danger` on a blocked one — the value the
 * block action operates on, and the reason an operator can be offered the right
 * one of two verbs instead of guessing. Colour is not the carrier: the label is
 * the state, written out.
 */
export function AccountStateBadge({ blocked }: { blocked: boolean }) {
  return (
    <StateBadge kind={blocked ? 'danger' : 'success'}>
      {blocked ? sharedCopy.accountState.blocked : sharedCopy.accountState.active}
    </StateBadge>
  );
}

/**
 * How many rows are shown against how many exist.
 *
 * **Not an empty state and not a pager.** No catalog row gives the account
 * register a traversal control — `/inbox` is the one surface the catalog grants
 * one — so this surface states its own truncation instead of inventing a
 * control, and it states it even when the two figures are equal: a line that
 * appears only on truncation teaches the operator to read its absence as
 * completeness on a page where it simply was not rendered.
 */
export function TruncationLine({ shown, total }: { shown: number; total: number }) {
  return (
    <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">
      {sharedCopy.truncation.prefix} <bdi>{formatNumber(shown)}</bdi> {sharedCopy.truncation.middle}{' '}
      <bdi>{formatNumber(total)}</bdi>.
    </p>
  );
}

/**
 * One failed read, rendered against its own band.
 *
 * A panel-level failure never blanks a page: the account detail composes three
 * independent reads, so a `backend-finance` outage takes the entitlement panel
 * down and leaves the profile, the role control, the biography, and the case
 * history usable. The tone comes from the resolved copy rather than from the
 * call site — a `502` is neutral because the failure is upstream and nobody in
 * this console caused it.
 */
export function SurfaceError({
  error,
  catalog,
  onRetry,
}: {
  error: unknown;
  catalog: Readonly<Record<string, ErrorCopy>>;
  onRetry: () => void;
}) {
  const status = error instanceof ApiError ? error.status : 0;
  const code = error instanceof ApiError ? error.code : undefined;
  const resolved = resolveErrorCopy(code, status, catalog);

  return (
    <ErrorBanner
      message={resolved.message}
      tone={resolved.tone === 'danger' ? 'danger' : 'neutral'}
      onRetry={onRetry}
    />
  );
}

/** A section of the account detail — a heading row with its one action, then its body. */
export function DetailSection({
  id,
  heading,
  action,
  context,
  children,
}: {
  id: string;
  heading: string;
  /** The section's own verb. Every action sits on its section's heading row. */
  action?: ReactNode;
  /** The one consequence the operator cannot see from the screen. */
  context?: string;
  children: ReactNode;
}) {
  return (
    <section aria-labelledby={id} className="mt-section-gap first:mt-0">
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
        <h2 id={id} className="font-display text-h2 font-bold">
          {heading}
        </h2>
        {action ? <span className="ms-auto">{action}</span> : null}
      </div>

      {context ? <p className="mb-4 max-w-measure text-xs text-ink-muted">{context}</p> : null}

      {children}
    </section>
  );
}

/** The secondary verb on a section's heading row. Never primary — the top bar holds that. */
export function SectionAction({
  onClick,
  icon,
  children,
  tone = 'secondary',
}: {
  onClick: () => void;
  icon?: ReactNode;
  children: ReactNode;
  /**
   * `danger` is outline-only and takes the danger colour on the label and the
   * border — never a fill. A filled red control is the affirmative primary
   * action, and `--danger` sits six degrees from `--accent-text`: at the same
   * lightness a filled destructive button is indistinguishable from «play».
   */
  tone?: 'secondary' | 'danger';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'hit-target flex items-center gap-2 rounded-sm border px-4 text-sm font-semibold',
        tone === 'danger'
          ? 'border-danger text-danger hover:bg-danger-soft'
          : 'border-border-strong bg-surface-raised text-ink hover:bg-surface-overlay',
      )}
    >
      {icon}
      {children}
    </button>
  );
}

/**
 * The card the detail's read-only panels sit in, and the fact list inside it.
 *
 * Both moved to the shared ring on their third surface — this detail, the report
 * entry, and the case detail draw one shape — and are re-exported here under the
 * names this area's call sites already use.
 */
export { Panel as DetailCard, MetaGrid } from '@shared/ui/Panel';
